import { h } from 'preact';
import jsonpatch from 'fast-json-patch';
import Store from 'store/store';
import { getAllMovies, getAllSections, patch, post } from 'utils/api';
import { handleConflicts } from 'utils/conflictResolutionModal';
import { getHubData } from 'utils/dynamicData';
import * as jsonPointer from 'utils/jsonPointer';
import { createAndUploadShareImage } from 'utils/media';
import { toast } from 'utils/modal';
import { getMovieStatus, getAllMovieStatuses } from 'utils/movieStatus';
import slugify from 'utils/slugify';
import { isFocusStudio } from 'utils/studioConfig';
import { getSyncThundrDataIfAvailable } from 'utils/thundr';
import { accessNested } from 'utils/utils';

const isEqual = (a, b) => {
	if (typeof a !== 'object' || typeof b !== 'object') {
		return a === b;
	}
	return a && b && !jsonpatch.compare(a, b).length;
};

function setSavedDataAndMerge(newData, path) {
	let hasChanged = false;

	// Ensure some global states are updated
	['published_status', 'ready_state', 'archived'].forEach(key => {
		const itemPath = `${path}/${key}`;
		const savedValue = jsonPointer.get(Store.get().savedData, itemPath);
		if (newData?.[key] && savedValue && newData[key] !== savedValue) {
			jsonPointer.set(Store.get().savedData, itemPath, newData[key]);
			jsonPointer.set(Store.get().data, itemPath, newData[key]);
			hasChanged = true;
		}
	});

	// TODO: Error ?
	if (!newData?.content) return hasChanged;

	const parent = jsonPointer.parse(path).slice();
	const key = parent.pop();

	const saved = jsonPointer.get(Store.get().savedData, path);
	if (!saved?.content) return hasChanged;

	let diff = jsonpatch.compare(saved.content || {}, newData.content || {});
	// No new changes
	if (!diff.length) return hasChanged;

	const currentEdited = jsonPointer.get(Store.get().data, path)?.toJS() || {};

	// Check conflicts and updates current to match newData when possible
	let conflicts = diff.filter(change => {
		let savedValue = jsonPointer.get(saved.content, change.path);
		let editedValue = jsonPointer.get(currentEdited.content, change.path);
		let newValue = jsonPointer.get(newData.content, change.path);
		if (isEqual(newValue, editedValue)) return false;
		if (isEqual(savedValue, editedValue)) {
			let parent = jsonPointer.parse(change.path);
			let attribute = parent.pop();
			if (change.op === 'remove') {
				delete accessNested(currentEdited.content, parent)[attribute];
			} else {
				accessNested(currentEdited.content, parent)[attribute] = newValue;
			}
			return false;
		}
		return true;
	});

	jsonPointer.get(Store.get().savedData, parent)?.set(key, newData);
	const newCurrent = structuredClone(newData);
	newCurrent.content = currentEdited.content;
	// NOTE: We might need to clean up old conflicts if the new conflicts affect the same paths
	newCurrent.conflicts = [].concat(currentEdited.conflicts || [], conflicts);
	jsonPointer.get(Store.get().data, parent)?.set(key, newCurrent);
	return true;
}

export function getDataPathFromItem(item) {
	if (!item?.id) return null;
	if (item.section_type === 'navigation') {
		return jsonPointer.compile(['navigation']);
	} else if (item.section_type === 'page') {
		return jsonPointer.compile(['pages', item.id]);
	}
	return jsonPointer.compile(['movies', item.id]);
}

function updateStoreAtPath(obj, path, data) {
	path = Array.isArray(path) ? path.slice() : jsonPointer.parse(path);
	const key = path.pop();
	jsonPointer.get(obj, path)?.set(key, data);
}
export function setData(path, data) {
	updateStoreAtPath(Store.get().data, path, data);
}
export function setSavedData(path, data) {
	updateStoreAtPath(Store.get().savedData, path, data);
}

export function revertData(path) {
	const store = Store.get();
	const saved = jsonPointer.get(store.savedData, path)?.toJS();
	if (saved) updateStoreAtPath(store.data, path, saved);

	// Clear the queue since we reverted the data
	Store.get().set({ mediaUpdateQueue: [] });
}

export function updateAllSavedData(movies, sections) {
	// const pages = sections.filter(section => section.section_type === 'page');
	// const navigation = sections.find(section => section.section_type === 'navigation');
	// const data = { movies: indexed(movies), pages: indexed(pages), navigation };
}

export async function updateSingleSavedData(item) {
	let at = getDataPathFromItem(item);
	if (!at || !item.content) return;
	at = jsonPointer.parse(at);
	setSavedData(at.concat('content'), item.content);

	const commit = item.commits?.current || { id: crypto.randomUUID(), at: +new Date() };
	// For now update the live data as well since some things use it, but might be good to change that (see note in loadContent of universalCMSApp)
	setData(at.concat('commits', 'current'), commit);
	setSavedData(at.concat('commits', 'current'), commit);

	if (item.id && at[0] === 'movies') {
		Store.get().movieStatuses?.set(item.id, await getMovieStatus(item)).now();
	}
}

export function updateSinglePublishData(item) {
	// Approximate for now, /publish does not currently return the new commit data
	const commit = { id: crypto.randomUUID(), at: +new Date() };
	let at = getDataPathFromItem(item);
	if (!at) return;
	at = jsonPointer.parse(at);
	setData(at.concat('commits', 'publish'), commit);
	setSavedData(at.concat('commits', 'publish'), commit);
}

export function hasUnsavedChanges(path) {
	const store = Store.get();
	const data = jsonPointer.get(store.data, path);
	const savedData = jsonPointer.get(store.savedData, path);
	const diff = jsonpatch.compare(savedData?.content || {}, data?.content || {});
	return diff.length;
}

export async function save(path) {
	const parsedPath = jsonPointer.parse(path);
	const type = parsedPath[0] === 'movies' ? 'movies' : 'sections';

	if (type === 'movies') {
		await refreshMovies();
	} else {
		await refreshSections();
	}

	// Clear pending conflicts
	const conflictsResults = await handleConflicts(path);
	if (conflictsResults === 'CANCEL') return;

	const store = Store.get();
	const data = jsonPointer.get(store.data, path);
	const savedData = jsonPointer.get(store.savedData, path);
	if (!data?.id || !savedData) {
		throw new Error(`Data not found at path ${path}`);
	}
	const diff = jsonpatch.compare(savedData.content, data.content);
	if (!diff.length) {
		return;
	}

	const result = await patch(`/${type}/${data.id}`, {
		patches: diff,
	});
	await updateSingleSavedData(result);

	try {
		// runs any queued media updates (see enqueue() in utils/media.js)
		// (e.g. when assigning media to a section/movie)
		const mediaUpdateQueue = Store.get().mediaUpdateQueue;
		if (mediaUpdateQueue?.length) {
			await Promise.all(
				mediaUpdateQueue.map(async (item) => {
					return await patch(`/media/${item.id}`, item.details);
				})
			);
			Store.get().set({ mediaUpdateQueue: [] });
		}
	} catch (err) {
		console.error(err);
	}
}

export async function updateMovieShareImage(movieId) {
	const movieData = jsonPointer.get(Store.get().data, ['movies', movieId]);
	if (!movieData) return false;

	try {
		return await createAndUploadShareImage(movieId);
	} catch (err) {
		console.warn('Unable to update share image:', err);
		return false;
	}
}

export async function beforePublish(path) {
	// Run any actions that need to happen before publish, such as validating data or saving related data
	let requiresSave = false;

	const isMoviePublish = path.startsWith('/movies/');
	if (isMoviePublish) {
		const movieId = path.split('/').pop();
		if (movieId) {
			const imageUpdated = await updateMovieShareImage(movieId);
			if (imageUpdated) requiresSave = true;
		}
	}

	return requiresSave;
}

export async function publish(path) {
	const parsedPath = jsonPointer.parse(path);
	const type = parsedPath[0] === 'movies' ? 'movies' : 'sections';
	const data = jsonPointer.get(Store.get().data, path);
	if (!data?.id) {
		throw new Error(`Data not found at path ${path}`);
	}
	const item = await post(`/${type}/publish/${data.id}`, {});
	updateSinglePublishData(item);
	Store.emit('PUBLISH_SUCCESS');
}

export async function unpublish(path, redirectUrl) {
	const data = jsonPointer.get(Store.get().data, path);
	if (!data?.id) {
		throw new Error(`Data not found at path ${path}`);
	}
	const item = await post(`/movies/unpublish/${data.id}`, { redirectUrl });
	updateSinglePublishData(item);
	Store.emit('PUBLISH_SUCCESS');
}

export async function createMovie(options) {
	const title = options?.title || undefined;
	const release = options?.release || null;
	const slug = options?.slug || slugify(title, { removeDiacritics: true });
	const url = options?.url || null;
	const base_movie_id = options?.baseId || undefined; // Must be undefined if not provided

	const creatingToast = toast('Creating new movie...', { icon: 'spinner', stubborn: true });

	const newMovieData = {
		title,
		content: {
			title,
			slug,
			url,
			dates: {
				homeEnt: null,
				release,
				ticketing: null,
				hideFromCarouselsAfter: null,
			},
		},
		published_status: 'DRAFT_PLACEHOLDER',
		ready_state: 'none',
		thundr_id: null,
		base_movie_id,
	};

	if (!base_movie_id && options?.base) {
		const baseTitle = options.base.title || 'New Movie';

		newMovieData.base_movie = {
			title: baseTitle,
			content: {
				title: baseTitle,
			},
		};
	}

	if (isFocusStudio()) {
		newMovieData.content.pages = {
			home: {
				hero: {
					cta: { enabled: true },
				},
				settings: { default: true },
				metadata: {
					openGraphDescriptionIsMetaDescription: true,
					openGraphTitleIsHtmlTitle: true,
				},
			},
			synopsis: {
				credits: {
					cast: {
						order: 1,
						label: 'STARRING',
						credits: {
							new: { name: 'Add cast...', order: 1 },
						},
					},
					directors: {
						order: 2,
						label: 'DIRECTED BY',
						credits: {
							new: { name: 'Add director...', order: 1 },
						},
					},
					writers: {
						order: 3,
						label: 'WRITTEN BY',
						credits: {
							new: { name: 'Add writer...', order: 1 },
						},
					},
					producers: {
						order: 4,
						label: 'PRODUCED BY',
						credits: {
							new: { name: 'Add producer...', order: 1 },
						},
					},
				},
			},
		};
	}

	// Let errors bubble up to the caller
	const newMovie = await post('/movies', newMovieData);
	if (!newMovie?.id) {
		creatingToast.close();
		toast('Failed to create new movie', { icon: 'cancel' });
		throw new Error('Failed to create new movie');
	}
	if (!newMovie.commits) {
		newMovie.commits = { current: { id: crypto.randomUUID(), at: +new Date() }, publish: null };
	}

	Store.get().data?.movies?.set(newMovie.id, newMovie);
	Store.get().savedData?.movies?.set(newMovie.id, newMovie);

	creatingToast.close();
	const newMovieUrl = `#movies/${newMovie.base_movie_id || newMovie.id}`;
	toast(<span>New movie created! <a href={newMovieUrl}>View movie</a></span>, { icon: 'check', duration: 5000 });

	return newMovie;
}

let pendingMovieRefresh;
let moviesRefreshTimer;
async function _refreshMovies() {
	const lastUpdate = Store.get().savedData?.lastMoviesUpdate;
	const since = lastUpdate ? new Date(lastUpdate).toISOString() : undefined;
	const movies = await getAllMovies({ qs: { since } });
	Store.emit('UNDO_REDO_EDITING_PROGRAMATICALLY', true);
	let updated = movies.reduce((result, movie) => {
		let updatedCurrent = setSavedDataAndMerge(movie, getDataPathFromItem(movie));
		return result || updatedCurrent;
	}, false);
	if (updated) {
		Store.get().savedData.set('lastMoviesUpdate', Date.now()).now();
	}
	Store.emit('UNDO_REDO_EDITING_PROGRAMATICALLY', false);
}
export function refreshMovies() {
	clearTimeout(moviesRefreshTimer);
	if (!pendingMovieRefresh) {
		pendingMovieRefresh = _refreshMovies().then(r => {
			pendingMovieRefresh = null;
			return r;
		}, err => {
			pendingMovieRefresh = null;
			throw err;
		});
	}
	if (moviesRefreshTimer) scheduleMoviesRefresh();
	return pendingMovieRefresh;
}
export function scheduleMoviesRefresh(instantStart) {
	clearTimeout(moviesRefreshTimer);
	moviesRefreshTimer = setTimeout(refreshMovies, instantStart ? 0 : 5000);
}
export function stopMoviesRefresh() {
	clearTimeout(moviesRefreshTimer);
	moviesRefreshTimer = null;
}

// Very similar to the movies version above - TODO: refactor to avoid that?
let pendingSectionsRefresh;
let sectionsRefreshTimer;
async function _refreshSections() {
	const lastUpdate = Store.get().savedData?.lastSectionsUpdate;
	const since = lastUpdate ? new Date(lastUpdate).toISOString() : undefined;
	const sections = await getAllSections({ qs: { since } });
	Store.emit('UNDO_REDO_EDITING_PROGRAMATICALLY', true);
	let updated = sections.reduce((result, section) => {
		let updatedCurrent = setSavedDataAndMerge(section, getDataPathFromItem(section));
		return result || updatedCurrent;
	}, false);
	if (updated) {
		Store.get().savedData.set('lastSectionsUpdate', Date.now()).now();
	}
	Store.emit('UNDO_REDO_EDITING_PROGRAMATICALLY', false);
}
export function refreshSections() {
	clearTimeout(sectionsRefreshTimer);
	if (!pendingSectionsRefresh) {
		pendingSectionsRefresh = _refreshSections().then(r => {
			pendingSectionsRefresh = null;
			return r;
		}, err => {
			pendingSectionsRefresh = null;
			throw err;
		});
	}
	if (sectionsRefreshTimer) scheduleSectionsRefresh();
	return pendingSectionsRefresh;
}
export function scheduleSectionsRefresh(instantStart) {
	clearTimeout(sectionsRefreshTimer);
	sectionsRefreshTimer = setTimeout(refreshSections, instantStart ? 0 : 5000);
}
export function stopSectionsRefresh() {
	clearTimeout(sectionsRefreshTimer);
	sectionsRefreshTimer = null;
}

// TODO: Store urls in database rather than hardcoded here?
export const hubPageUrls = new Map([
	['landing', '/'],
	['news', '/news/'],
	['leadership', '/leadership-team/'],
	['about', '/about/'],
	['contact', '/contact/'],
	['releases', '/movies/'],
]);
const thundrIds = new Map([
	['home', 'boltHome'],
	['showtimes', 'boltShowtimes'],
	['home-ent', 'boltHomeEnt'],
]);
const movieUrls = new Map([
	['home', '/'],
	['showtimes', '/tickets/'],
	['home-ent', '/watch-at-home/'],
]);
function getPagePath(page, thundrData) {
	return thundrData?.pages?.[thundrIds.get(page) || page]?.path || movieUrls.get(page) || '/';
}
export function getLiveUrlFromPageData(data) {
	if (!data) return null;
	const slug = data.content?.slug || data.slug;
	if (data.section_type === 'navigation') return '/';
	if (data.section_type === 'page') {
		return hubPageUrls.get(slug) || '/';
	}

	const store = Store.get();
	const studio = store.domains;
	if (studio?.isUniversal && isPlaceholder(data)) return '/movies/movie-placeholder/';

	const moviePath = studio?.isFocus ? '/films/' : '/movies/';

	if (!slug) return moviePath;
	const currentCmsPage = store.path?.at(-1);
	const liveData = store.publishedData?.movies?.[data.id] || data;
	return `${moviePath}${slug}${getPagePath(currentCmsPage, getSyncThundrDataIfAvailable(liveData))}`;
}
export function getLiveUrlFromPath(path) {
	return getLiveUrlFromPageData(jsonPointer.get(Store.get().data, path));
}

export async function upgradeMovie(id) {
	const result = await patch(`/movies/upgrade/${id}`, {});
	const newPublishedStatus = result?.published_status;
	const publishedStatusPath = ['movies', id, 'published_status'];
	setData(publishedStatusPath, newPublishedStatus);
	setSavedData(publishedStatusPath, newPublishedStatus);

	// Clear hideFromCarouselsAfter when upgrading from placeholder to full movie
	// This ensures the toggle is OFF by default
	const hideFromCarouselsPath = ['movies', id, 'content', 'dates', 'hideFromCarouselsAfter'];
	setData(hideFromCarouselsPath, null);
	setSavedData(hideFromCarouselsPath, null);

	// Prevent any undoing
	Store.get().now();
	Store.emit('UNDO_REDO_CLEAR_STACK');
}

export function isPlaceholder(movie) {
	return movie?.ready_state ? movie.ready_state === 'none' : movie?.published_status?.endsWith('_PLACEHOLDER');
}

export async function getPublishedMovies() {
	const data = await getHubData('all', 'movie');
	const statuses = await getAllMovieStatuses(data);

	return Object.fromEntries(data.map(movie => {
		const id = movie.id || movie.movie_id;
		const status = statuses?.[id];
		const placeholder = isPlaceholder(movie);
		return [id, { ...movie, id, status, placeholder }];
	}));
}